import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../components/Admin/AdminContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  MapPin, Search, MoreHorizontal, Eye, EyeOff, 
  Edit, Trash2, Calendar, User, Heart, MessageCircle,
  Image as ImageIcon, Navigation, Car, Clock, Users,
  Sun, Cloud, Compass, X, Plus, Save, ChevronDown, ChevronUp,
  Sparkles, ScanSearch, ExternalLink
} from 'lucide-react';

export default function AdminSpots() {
  const { adminAPI } = useAdmin();
  const [spots, setSpots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedSpot, setSelectedSpot] = useState(null);
  const [showSpotModal, setShowSpotModal] = useState(false);
  const [editingSpot, setEditingSpot] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [scanningSpot, setScanningSpot] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [spotsPerPage] = useState(20);

  useEffect(() => {
    loadSpots();
  }, [currentPage, searchTerm, statusFilter]);

  const loadSpots = async () => {
    try {
      setLoading(true);
      const params = {
        skip: currentPage * spotsPerPage,
        limit: spotsPerPage,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter !== 'all' && { status_filter: statusFilter })
      };
      
      const data = await adminAPI.getSpots(params);
      setSpots(data);
    } catch (error) {
      console.error('Failed to load spots:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSpotAction = async (action, spotId, data = {}) => {
    try {
      setActionLoading(true);
      
      switch (action) {
        case 'disable':
          await adminAPI.disableSpot(spotId, data.reason);
          break;
        case 'enable':
          await adminAPI.enableSpot(spotId);
          break;
        case 'delete':
          await adminAPI.deleteSpot(spotId);
          break;
        case 'update':
          await adminAPI.updateSpot(spotId, data);
          break;
        default:
          throw new Error('Action non reconnue');
      }
      
      await loadSpots();
      setShowSpotModal(false);
      setEditingSpot(null);
      
    } catch (error) {
      console.error(`Failed to ${action} spot:`, error);
      alert(`Erreur lors de l'action: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: { class: 'bg-green-100 text-green-800', label: 'Actif' },
      inactive: { class: 'bg-yellow-100 text-yellow-800', label: 'Inactif' },
      under_review: { class: 'bg-blue-100 text-blue-800', label: 'En révision' },
      deleted: { class: 'bg-red-100 text-red-800', label: 'Supprimé' }
    };
    
    const variant = variants[status] || variants.active;
    return <Badge className={variant.class}>{variant.label}</Badge>;
  };

  // Scanner un spot avec l'IA (optimisation contenu)
  const handleAIScan = async (spotId) => {
    const confirmed = window.confirm(
      '🎨 Scanner ce spot avec l\'IA ?\n\n' +
      'L\'IA va analyser et optimiser le titre, la description et les tags.\n' +
      'Les résultats seront disponibles dans "Modifications IA" dans 30-60 secondes.'
    );
    
    if (!confirmed) return;
    
    try {
      setScanningSpot(spotId);
      const token = localStorage.getItem('adminToken');
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/scan/${spotId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        alert('✅ Scan IA enregistré !\n\nLe worker va traiter ce spot dans les 30-60 prochaines secondes.\nConsultez l\'onglet "Modifications IA" pour valider les suggestions.');
        setTimeout(() => loadSpots(), 2000);
      } else {
        const error = await response.json();
        alert(`❌ Erreur: ${error.detail || 'Échec du scan IA'}`);
      }
    } catch (error) {
      console.error('Error scanning spot:', error);
      alert('❌ Erreur lors du scan IA');
    } finally {
      setScanningSpot(null);
    }
  };

  // Scanner la validation géographique
  const handleGeoScan = async (spotId) => {
    const confirmed = window.confirm(
      '🌍 Lancer une validation géographique ?\n\n' +
      'L\'IA va vérifier la cohérence entre les photos et les coordonnées GPS.\n' +
      'Les résultats seront disponibles dans "Validation Géo" dans 30-60 secondes.'
    );
    
    if (!confirmed) return;
    
    try {
      setScanningSpot(spotId);
      const token = localStorage.getItem('adminToken');
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/geo/scan/${spotId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        alert('✅ Validation géographique enregistrée !\n\nLe worker va traiter ce spot dans les 30-60 prochaines secondes.\nConsultez l\'onglet "Validation Géo" pour voir les résultats.');
        setTimeout(() => loadSpots(), 2000);
      } else {
        const error = await response.json();
        alert(`❌ Erreur: ${error.detail || 'Échec de la validation géographique'}`);
      }
    } catch (error) {
      console.error('Error geo scanning spot:', error);
      alert('❌ Erreur lors de la validation géographique');
    } finally {
      setScanningSpot(null);
    }
  };

  // Modal pour les actions rapides (désactiver, supprimer)
  const SpotActionModal = ({ spot, onAction }) => {
    const [actionType, setActionType] = useState('');
    const [reason, setReason] = useState('');

    const handleSubmit = (e) => {
      e.preventDefault();
      onAction(actionType, spot.id, { reason });
    };

    return (
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Actions sur le spot</DialogTitle>
          <DialogDescription>
            Spot: {spot.title} (par {spot.author})
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Action à effectuer</Label>
            <Select value={actionType} onValueChange={setActionType}>
              <SelectTrigger>
                <SelectValue placeholder="Choisir une action" />
              </SelectTrigger>
              <SelectContent>
                {spot.spot_status === 'active' && (
                  <SelectItem value="disable">Désactiver</SelectItem>
                )}
                {spot.spot_status !== 'active' && spot.spot_status !== 'deleted' && (
                  <SelectItem value="enable">Réactiver</SelectItem>
                )}
                <SelectItem value="delete">Supprimer</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {(actionType === 'disable' || actionType === 'delete') && (
            <div>
              <Label>Raison {actionType === 'disable' ? '(obligatoire)' : '(optionnel)'}</Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Expliquez la raison de cette action"
                required={actionType === 'disable'}
              />
            </div>
          )}

          <div className="flex gap-2 pt-4">
            <Button 
              type="submit" 
              disabled={!actionType || actionLoading}
              variant={actionType === 'delete' ? 'destructive' : 'default'}
              className="flex-1"
            >
              {actionLoading ? 'En cours...' : 'Confirmer'}
            </Button>
            <Button type="button" variant="outline" onClick={() => setShowSpotModal(false)}>
              Annuler
            </Button>
          </div>
        </form>
      </DialogContent>
    );
  };

  // Modal d'édition complète du spot
  const SpotEditModal = ({ spot, onSave }) => {
    const [editData, setEditData] = useState({
      // Informations de base
      title: spot.title || '',
      description: spot.description || '',
      author: spot.author || '',
      is_anonymous: spot.is_anonymous || false,
      created_at: spot.created_at ? new Date(spot.created_at).toISOString().slice(0, 16) : '',
      
      // Localisation
      latitude: spot.latitude || 0,
      longitude: spot.longitude || 0,
      parking_latitude: spot.parking_latitude || '',
      parking_longitude: spot.parking_longitude || '',
      
      // Tags
      tags: spot.tags || [],
      
      // Informations pratiques
      practical_info: {
        recommended_hours: spot.practical_info?.recommended_hours || '',
        parking_distance: spot.practical_info?.parking_distance || '',
        parking_info: spot.practical_info?.parking_info || '',
        occupation_level: spot.practical_info?.occupation_level || 'Modéré',
        accessibility: spot.practical_info?.accessibility || '',
        best_weather: spot.practical_info?.best_weather || ''
      },
      
      // Photos
      photos: spot.photos || [],
      parking_photos: spot.parking_photos || [],
      
      // Commentaires
      comments: spot.comments || [],
      
      // Statut
      spot_status: spot.spot_status || 'active'
    });

    const [newTag, setNewTag] = useState('');
    const [newPhotoUrl, setNewPhotoUrl] = useState('');
    const [newParkingPhotoUrl, setNewParkingPhotoUrl] = useState('');
    const [activeTab, setActiveTab] = useState('general');
    const [expandedPhoto, setExpandedPhoto] = useState(null);

    // Ajouter un tag
    const addTag = () => {
      if (newTag.trim() && !editData.tags.includes(newTag.trim())) {
        setEditData(prev => ({
          ...prev,
          tags: [...prev.tags, newTag.trim()]
        }));
        setNewTag('');
      }
    };

    // Supprimer un tag
    const removeTag = (tagToRemove) => {
      setEditData(prev => ({
        ...prev,
        tags: prev.tags.filter(tag => tag !== tagToRemove)
      }));
    };

    // Ajouter une photo
    const addPhoto = (isParking = false) => {
      const url = isParking ? newParkingPhotoUrl : newPhotoUrl;
      if (url.trim()) {
        const newPhoto = {
          url: url.trim(),
          metadata: {
            date: '',
            time: '',
            season: '',
            orientation: '',
            weather: '',
            conditions: ''
          }
        };
        
        if (isParking) {
          setEditData(prev => ({
            ...prev,
            parking_photos: [...prev.parking_photos, newPhoto]
          }));
          setNewParkingPhotoUrl('');
        } else {
          setEditData(prev => ({
            ...prev,
            photos: [...prev.photos, newPhoto]
          }));
          setNewPhotoUrl('');
        }
      }
    };

    // Supprimer une photo
    const removePhoto = (index, isParking = false) => {
      if (isParking) {
        setEditData(prev => ({
          ...prev,
          parking_photos: prev.parking_photos.filter((_, i) => i !== index)
        }));
      } else {
        setEditData(prev => ({
          ...prev,
          photos: prev.photos.filter((_, i) => i !== index)
        }));
      }
    };

    // Mettre à jour les métadonnées d'une photo
    const updatePhotoMetadata = (index, field, value, isParking = false) => {
      const photoArray = isParking ? 'parking_photos' : 'photos';
      setEditData(prev => ({
        ...prev,
        [photoArray]: prev[photoArray].map((photo, i) => {
          if (i === index) {
            return {
              ...photo,
              metadata: {
                ...photo.metadata,
                [field]: value
              }
            };
          }
          return photo;
        })
      }));
    };

    // Supprimer un commentaire
    const removeComment = (commentId) => {
      setEditData(prev => ({
        ...prev,
        comments: prev.comments.filter(comment => comment.id !== commentId)
      }));
    };

    // Mettre à jour un commentaire
    const updateComment = (commentId, field, value) => {
      setEditData(prev => ({
        ...prev,
        comments: prev.comments.map(comment => {
          if (comment.id === commentId) {
            return { ...comment, [field]: value };
          }
          return comment;
        })
      }));
    };

    // Soumettre les modifications
    const handleSubmit = (e) => {
      e.preventDefault();
      
      const updateData = {
        title: editData.title,
        description: editData.description,
        author: editData.author,
        is_anonymous: editData.is_anonymous,
        latitude: parseFloat(editData.latitude),
        longitude: parseFloat(editData.longitude),
        parking_latitude: editData.parking_latitude ? parseFloat(editData.parking_latitude) : null,
        parking_longitude: editData.parking_longitude ? parseFloat(editData.parking_longitude) : null,
        tags: editData.tags,
        practical_info: editData.practical_info,
        photos: editData.photos,
        parking_photos: editData.parking_photos,
        comments: editData.comments,
        spot_status: editData.spot_status
      };
      
      onSave('update', spot.id, updateData);
    };

    return (
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="h-5 w-5" />
            Modifier le spot complet
          </DialogTitle>
          <DialogDescription>
            Modification de "{spot.title}" - Tous les champs sont éditables
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex-1 overflow-hidden flex flex-col">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="grid grid-cols-5 w-full">
              <TabsTrigger value="general">Général</TabsTrigger>
              <TabsTrigger value="location">Localisation</TabsTrigger>
              <TabsTrigger value="practical">Infos pratiques</TabsTrigger>
              <TabsTrigger value="photos">Photos ({editData.photos.length})</TabsTrigger>
              <TabsTrigger value="comments">Commentaires ({editData.comments.length})</TabsTrigger>
            </TabsList>

            <div className="flex-1 overflow-y-auto mt-4 pr-2">
              {/* Onglet Général */}
              <TabsContent value="general" className="space-y-4 mt-0">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label className="text-sm font-medium">Titre du spot *</Label>
                    <Input
                      value={editData.title}
                      onChange={(e) => setEditData(prev => ({...prev, title: e.target.value}))}
                      placeholder="Ex: Lac de montagne paisible"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium">Auteur/Créateur</Label>
                    <Input
                      value={editData.author}
                      onChange={(e) => setEditData(prev => ({...prev, author: e.target.value}))}
                      placeholder="Nom de l'auteur"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium">Statut</Label>
                    <Select 
                      value={editData.spot_status} 
                      onValueChange={(value) => setEditData(prev => ({...prev, spot_status: value}))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Actif</SelectItem>
                        <SelectItem value="inactive">Inactif</SelectItem>
                        <SelectItem value="under_review">En révision</SelectItem>
                        <SelectItem value="deleted">Supprimé</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium">Description *</Label>
                  <Textarea
                    value={editData.description}
                    onChange={(e) => setEditData(prev => ({...prev, description: e.target.value}))}
                    placeholder="Description détaillée du spot..."
                    rows={4}
                    required
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_anonymous"
                    checked={editData.is_anonymous}
                    onChange={(e) => setEditData(prev => ({...prev, is_anonymous: e.target.checked}))}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="is_anonymous" className="text-sm cursor-pointer">
                    Publication anonyme
                  </Label>
                </div>

                {/* Tags */}
                <div>
                  <Label className="text-sm font-medium">Tags</Label>
                  <div className="flex flex-wrap gap-2 mt-2 mb-2">
                    {editData.tags.map((tag, index) => (
                      <Badge key={index} variant="secondary" className="flex items-center gap-1 px-3 py-1">
                        #{tag}
                        <button 
                          type="button" 
                          onClick={() => removeTag(tag)}
                          className="ml-1 hover:text-red-600"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      placeholder="Ajouter un tag..."
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    />
                    <Button type="button" variant="outline" onClick={addTag}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </TabsContent>

              {/* Onglet Localisation */}
              <TabsContent value="location" className="space-y-4 mt-0">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-green-600" />
                      Coordonnées du spot
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium">Latitude *</Label>
                      <Input
                        type="number"
                        step="any"
                        value={editData.latitude}
                        onChange={(e) => setEditData(prev => ({...prev, latitude: e.target.value}))}
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Longitude *</Label>
                      <Input
                        type="number"
                        step="any"
                        value={editData.longitude}
                        onChange={(e) => setEditData(prev => ({...prev, longitude: e.target.value}))}
                        required
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Car className="h-5 w-5 text-blue-600" />
                      Coordonnées du parking (optionnel)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium">Latitude parking</Label>
                      <Input
                        type="number"
                        step="any"
                        value={editData.parking_latitude}
                        onChange={(e) => setEditData(prev => ({...prev, parking_latitude: e.target.value}))}
                        placeholder="Laisser vide si pas de parking"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Longitude parking</Label>
                      <Input
                        type="number"
                        step="any"
                        value={editData.parking_longitude}
                        onChange={(e) => setEditData(prev => ({...prev, parking_longitude: e.target.value}))}
                        placeholder="Laisser vide si pas de parking"
                      />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Onglet Informations pratiques */}
              <TabsContent value="practical" className="space-y-4 mt-0">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Heures recommandées
                    </Label>
                    <Input
                      value={editData.practical_info.recommended_hours}
                      onChange={(e) => setEditData(prev => ({
                        ...prev,
                        practical_info: {...prev.practical_info, recommended_hours: e.target.value}
                      }))}
                      placeholder="Ex: Lever de soleil (6h-8h), Coucher (18h-20h)"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Niveau d'affluence
                    </Label>
                    <Select 
                      value={editData.practical_info.occupation_level} 
                      onValueChange={(value) => setEditData(prev => ({
                        ...prev,
                        practical_info: {...prev.practical_info, occupation_level: value}
                      }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Très tranquille">Très tranquille</SelectItem>
                        <SelectItem value="Modéré">Modéré</SelectItem>
                        <SelectItem value="Populaire">Populaire</SelectItem>
                        <SelectItem value="Très populaire">Très populaire</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <Car className="h-4 w-4" />
                      Distance parking
                    </Label>
                    <Input
                      value={editData.practical_info.parking_distance}
                      onChange={(e) => setEditData(prev => ({
                        ...prev,
                        practical_info: {...prev.practical_info, parking_distance: e.target.value}
                      }))}
                      placeholder="Ex: 100m, 500m, 1km"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium">Infos parking</Label>
                    <Input
                      value={editData.practical_info.parking_info}
                      onChange={(e) => setEditData(prev => ({
                        ...prev,
                        practical_info: {...prev.practical_info, parking_info: e.target.value}
                      }))}
                      placeholder="Ex: Parking gratuit 20 places"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <Navigation className="h-4 w-4" />
                      Accessibilité
                    </Label>
                    <Input
                      value={editData.practical_info.accessibility}
                      onChange={(e) => setEditData(prev => ({
                        ...prev,
                        practical_info: {...prev.practical_info, accessibility: e.target.value}
                      }))}
                      placeholder="Ex: Facile - 5min de marche"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <Sun className="h-4 w-4" />
                      Météo idéale
                    </Label>
                    <Input
                      value={editData.practical_info.best_weather}
                      onChange={(e) => setEditData(prev => ({
                        ...prev,
                        practical_info: {...prev.practical_info, best_weather: e.target.value}
                      }))}
                      placeholder="Ex: Temps clair, après la pluie"
                    />
                  </div>
                </div>
              </TabsContent>

              {/* Onglet Photos */}
              <TabsContent value="photos" className="space-y-4 mt-0">
                {/* Photos du spot */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <ImageIcon className="h-5 w-5 text-green-600" />
                      Photos du spot ({editData.photos.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Liste des photos */}
                    <div className="grid grid-cols-2 gap-4">
                      {editData.photos.map((photo, index) => (
                        <Card key={index} className="overflow-hidden">
                          <div className="relative">
                            <img 
                              src={photo.url} 
                              alt={`Photo ${index + 1}`}
                              className="w-full h-32 object-cover"
                              onError={(e) => e.target.src = 'https://via.placeholder.com/300x200?text=Image+non+disponible'}
                            />
                            <Button
                              type="button"
                              size="sm"
                              variant="destructive"
                              className="absolute top-2 right-2"
                              onClick={() => removePhoto(index)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                          <CardContent className="p-3 space-y-2">
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label className="text-xs">Date</Label>
                                <Input
                                  type="date"
                                  value={photo.metadata?.date || ''}
                                  onChange={(e) => updatePhotoMetadata(index, 'date', e.target.value)}
                                  className="h-8 text-sm"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Heure</Label>
                                <Input
                                  type="time"
                                  value={photo.metadata?.time || ''}
                                  onChange={(e) => updatePhotoMetadata(index, 'time', e.target.value)}
                                  className="h-8 text-sm"
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label className="text-xs">Saison</Label>
                                <Select 
                                  value={photo.metadata?.season || ''} 
                                  onValueChange={(v) => updatePhotoMetadata(index, 'season', v)}
                                >
                                  <SelectTrigger className="h-8 text-sm">
                                    <SelectValue placeholder="Saison" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Printemps">Printemps</SelectItem>
                                    <SelectItem value="Été">Été</SelectItem>
                                    <SelectItem value="Automne">Automne</SelectItem>
                                    <SelectItem value="Hiver">Hiver</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Label className="text-xs">Orientation</Label>
                                <Select 
                                  value={photo.metadata?.orientation || ''} 
                                  onValueChange={(v) => updatePhotoMetadata(index, 'orientation', v)}
                                >
                                  <SelectTrigger className="h-8 text-sm">
                                    <SelectValue placeholder="Direction" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Nord">Nord</SelectItem>
                                    <SelectItem value="Nord-Est">Nord-Est</SelectItem>
                                    <SelectItem value="Est">Est</SelectItem>
                                    <SelectItem value="Sud-Est">Sud-Est</SelectItem>
                                    <SelectItem value="Sud">Sud</SelectItem>
                                    <SelectItem value="Sud-Ouest">Sud-Ouest</SelectItem>
                                    <SelectItem value="Ouest">Ouest</SelectItem>
                                    <SelectItem value="Nord-Ouest">Nord-Ouest</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            <div>
                              <Label className="text-xs">Météo/Conditions</Label>
                              <Input
                                value={photo.metadata?.weather || ''}
                                onChange={(e) => updatePhotoMetadata(index, 'weather', e.target.value)}
                                placeholder="Ex: Ensoleillé, brumeux..."
                                className="h-8 text-sm"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Notes/Conditions spéciales</Label>
                              <Input
                                value={photo.metadata?.conditions || ''}
                                onChange={(e) => updatePhotoMetadata(index, 'conditions', e.target.value)}
                                placeholder="Ex: Lever de soleil après nuit fraîche"
                                className="h-8 text-sm"
                              />
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>

                    {/* Ajouter une photo */}
                    <div className="flex gap-2 mt-4">
                      <Input
                        value={newPhotoUrl}
                        onChange={(e) => setNewPhotoUrl(e.target.value)}
                        placeholder="URL de la nouvelle photo (Unsplash, etc.)"
                        className="flex-1"
                      />
                      <Button type="button" variant="outline" onClick={() => addPhoto(false)}>
                        <Plus className="h-4 w-4 mr-2" />
                        Ajouter
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Photos du parking */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Car className="h-5 w-5 text-orange-600" />
                      Photos du parking ({editData.parking_photos.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      {editData.parking_photos.map((photo, index) => (
                        <Card key={index} className="overflow-hidden">
                          <div className="relative">
                            <img 
                              src={photo.url} 
                              alt={`Parking ${index + 1}`}
                              className="w-full h-32 object-cover"
                              onError={(e) => e.target.src = 'https://via.placeholder.com/300x200?text=Image+non+disponible'}
                            />
                            <Button
                              type="button"
                              size="sm"
                              variant="destructive"
                              className="absolute top-2 right-2"
                              onClick={() => removePhoto(index, true)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                          <CardContent className="p-3 space-y-2">
                            <div>
                              <Label className="text-xs">Conditions/Notes</Label>
                              <Input
                                value={photo.metadata?.conditions || ''}
                                onChange={(e) => updatePhotoMetadata(index, 'conditions', e.target.value, true)}
                                placeholder="Ex: Parking en gravier, 20 places"
                                className="h-8 text-sm"
                              />
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>

                    <div className="flex gap-2">
                      <Input
                        value={newParkingPhotoUrl}
                        onChange={(e) => setNewParkingPhotoUrl(e.target.value)}
                        placeholder="URL de la photo de parking"
                        className="flex-1"
                      />
                      <Button type="button" variant="outline" onClick={() => addPhoto(true)}>
                        <Plus className="h-4 w-4 mr-2" />
                        Ajouter
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Onglet Commentaires */}
              <TabsContent value="comments" className="space-y-4 mt-0">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <MessageCircle className="h-5 w-5 text-blue-600" />
                      Gestion des commentaires ({editData.comments.length})
                    </CardTitle>
                    <CardDescription>
                      Modérez et modifiez les commentaires des utilisateurs
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {editData.comments.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">Aucun commentaire sur ce spot</p>
                    ) : (
                      editData.comments.map((comment, index) => (
                        <Card key={comment.id || index} className="bg-gray-50">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1 space-y-2">
                                <div className="flex items-center gap-2">
                                  <Input
                                    value={comment.author}
                                    onChange={(e) => updateComment(comment.id, 'author', e.target.value)}
                                    className="w-40 h-8 text-sm font-medium"
                                    placeholder="Auteur"
                                  />
                                  <span className="text-xs text-gray-500">
                                    {comment.timestamp ? new Date(comment.timestamp).toLocaleString('fr-FR') : 'Date inconnue'}
                                  </span>
                                  <Badge variant="outline" className="text-xs">
                                    <Heart className="h-3 w-3 mr-1" />
                                    {comment.likes || 0}
                                  </Badge>
                                </div>
                                <Textarea
                                  value={comment.content}
                                  onChange={(e) => updateComment(comment.id, 'content', e.target.value)}
                                  className="text-sm"
                                  rows={2}
                                />
                              </div>
                              <Button
                                type="button"
                                size="sm"
                                variant="destructive"
                                onClick={() => removeComment(comment.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </div>
          </Tabs>

          {/* Footer avec boutons */}
          <div className="flex items-center justify-between pt-4 border-t mt-4">
            <div className="text-sm text-gray-500">
              Dernière modification: {spot.last_modified ? new Date(spot.last_modified).toLocaleString('fr-FR') : 'Jamais'}
              {spot.modified_by && ` par ${spot.modified_by}`}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setEditingSpot(null)}>
                Annuler
              </Button>
              <Button type="submit" disabled={actionLoading} className="bg-green-600 hover:bg-green-700">
                {actionLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Sauvegarder les modifications
                  </>
                )}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gestion des Spots</h1>
          <p className="text-gray-600 mt-1">Gérer tous les spots de la plateforme</p>
        </div>
        <div className="flex items-center gap-2">
          <MapPin className="h-5 w-5 text-green-600" />
          <span className="font-medium text-green-600">{spots.length} spots</span>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Rechercher par titre, description ou auteur..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <div className="w-full sm:w-48">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="active">Actifs</SelectItem>
                  <SelectItem value="inactive">Inactifs</SelectItem>
                  <SelectItem value="under_review">En révision</SelectItem>
                  <SelectItem value="deleted">Supprimés</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Spots Table */}
      <Card>
        <CardHeader>
          <CardTitle>Liste des Spots</CardTitle>
          <CardDescription>
            Cliquez sur un spot pour voir les détails et effectuer des actions
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 border-3 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                <span>Chargement des spots...</span>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {spots.map((spot) => (
                <div
                  key={spot.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-4 flex-1">
                    {/* Spot Image Preview */}
                    <div className="w-16 h-16 bg-gradient-to-r from-green-400 to-blue-500 rounded-lg flex items-center justify-center text-white flex-shrink-0">
                      {spot.photos && spot.photos.length > 0 ? (
                        <img 
                          src={spot.photos[0].url || spot.photos[0]} 
                          alt={spot.title}
                          className="w-full h-full object-cover rounded-lg"
                        />
                      ) : (
                        <MapPin className="h-8 w-8" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium text-gray-900 truncate">{spot.title}</h3>
                        {getStatusBadge(spot.spot_status || 'active')}
                        {spot.is_anonymous && (
                          <Badge variant="outline" className="text-xs">Anonyme</Badge>
                        )}
                      </div>

                      <p className="text-sm text-gray-600 truncate mb-2">{spot.description}</p>

                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {spot.author}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(spot.created_at).toLocaleDateString('fr-FR')}
                        </span>
                        <span className="flex items-center gap-1">
                          <Navigation className="h-3 w-3" />
                          {spot.latitude.toFixed(4)}, {spot.longitude.toFixed(4)}
                        </span>
                        {spot.parking_latitude && (
                          <span className="flex items-center gap-1">
                            <Car className="h-3 w-3" />
                            Parking
                          </span>
                        )}
                      </div>

                      {/* Tags */}
                      {spot.tags && spot.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {spot.tags.slice(0, 3).map((tag, index) => (
                            <Badge key={index} variant="secondary" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                          {spot.tags.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{spot.tags.length - 3}
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    {/* Stats */}
                    <div className="text-right text-sm text-gray-600 flex-shrink-0">
                      <div className="flex items-center gap-1">
                        <Heart className="h-3 w-3" />
                        {spot.likes || 0}
                      </div>
                      <div className="flex items-center gap-1">
                        <MessageCircle className="h-3 w-3" />
                        {spot.comments?.length || 0}
                      </div>
                      <div className="flex items-center gap-1">
                        <ImageIcon className="h-3 w-3" />
                        {spot.photos?.length || 0}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => window.open(`https://platform.sun-spot.app/spot/${spot.id}?details=true`, '_blank')}
                        title="Voir le rendu sur la plateforme"
                        className="text-green-600 hover:bg-green-50 border-green-200"
                      >
                        <ExternalLink className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleAIScan(spot.id)}
                        disabled={scanningSpot === spot.id}
                        title="Scanner avec l'IA (optimisation contenu)"
                        className="text-purple-600 hover:bg-purple-50 border-purple-200"
                      >
                        <Sparkles className={`h-3 w-3 ${scanningSpot === spot.id ? 'animate-pulse' : ''}`} />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleGeoScan(spot.id)}
                        disabled={scanningSpot === spot.id}
                        title="Validation géographique IA"
                        className="text-blue-600 hover:bg-blue-50 border-blue-200"
                      >
                        <ScanSearch className={`h-3 w-3 ${scanningSpot === spot.id ? 'animate-pulse' : ''}`} />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditingSpot(spot)}
                        title="Modifier le spot"
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedSpot(spot);
                          setShowSpotModal(true);
                        }}
                        title="Actions rapides"
                      >
                        <MoreHorizontal className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}

              {spots.length === 0 && (
                <div className="text-center py-8">
                  <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">Aucun spot trouvé</p>
                </div>
              )}
            </div>
          )}

          {/* Pagination */}
          <div className="flex items-center justify-between pt-4 border-t">
            <Button
              variant="outline"
              disabled={currentPage === 0}
              onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
            >
              Précédent
            </Button>
            <span className="text-sm text-gray-600">
              Page {currentPage + 1}
            </span>
            <Button
              variant="outline"
              disabled={spots.length < spotsPerPage}
              onClick={() => setCurrentPage(prev => prev + 1)}
            >
              Suivant
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Modals */}
      <Dialog open={showSpotModal} onOpenChange={setShowSpotModal}>
        {selectedSpot && (
          <SpotActionModal 
            spot={selectedSpot} 
            onAction={handleSpotAction}
          />
        )}
      </Dialog>

      <Dialog open={!!editingSpot} onOpenChange={(open) => !open && setEditingSpot(null)}>
        {editingSpot && (
          <SpotEditModal 
            spot={editingSpot} 
            onSave={handleSpotAction}
          />
        )}
      </Dialog>
    </div>
  );
}
